import React, { useRef, useState, useEffect } from 'react';
import { Card, Space, Button, Spin } from 'antd';
import { useSelector } from 'umi';
import type { UserState } from 'umi';
import { Modal } from 'antd-mobile'; // 添加导入
import clsx from 'clsx';
import HandleButton from '@/components/HandleButton';
import { formatDateTime2 } from '@/utils/dateutils';
import { usePersistFn } from 'ahooks';
import { useIntl } from '@@/plugin-locale/localeExports';
import { commonPostRequest } from '@/utils/apiUtils';
import { Helmet } from '@@/plugin-helmet/exports';
import styles from './css/ApplyRecordService.module.less';


// 定义开放日数据类型
interface OpendayItem {
  id: string;
  appNo: string;
  applicantName: string;
  spouseName: string;
  submitTime: string;
  applicantCellphone: string;
  applicantAreano: string;
  applicantAddress: string;
  flowStatus: number;
  cancelStatus: number;
}
// 獲取申請記錄數據
const fetchApplyList = async (params?: Record<string, unknown>): Promise<OpendayItem[]> => {
  return commonPostRequest<OpendayItem[]>('/api/adoptappli/applylist', params);
};


// 修改 cacelApply 函数为 POST 请求
const cacelApply = async (params?: Record<string, unknown>): Promise<unknown> => {
  return commonPostRequest<unknown>('/api/adoptappli/cancelApply', params);
};

function ApplyRecoedService(props: unknown) {
  const intl = useIntl();
  // 在组件内部添加引用
  const goBackModal = useRef<{ close: () => void } | null>(null);
  // 用于步骤切换时回到顶部操作
  const scrollRef = useRef<HTMLDivElement>(null);
  const [applyList, setApplyList] = useState<OpendayItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isAdd, setIsAdd] = useState(true);
  // 获取用户信息
  const { profile } = useSelector(({ user }: { user: UserState }) => user);

  const getStatusText = (flowStatus: number, cancelStatus?: number): string => {
    // 如果取消状态为1，直接显示已取消
    if (cancelStatus === 1) {
      return intl.formatMessage({ id: '已取消' });
    }
    switch (flowStatus) {
      case 0:
        return intl.formatMessage({ id: '草稿' });
      case 1:
        return intl.formatMessage({ id: '待評估' });
      case 2:
        return intl.formatMessage({ id: '評估完成' });
      default:
        return intl.formatMessage({ id: '未知狀態' });
    }
  };

  /**
   * 取消申請
   */
  const handleCancelApply = usePersistFn((applicationId: string) => {
    if (goBackModal.current) return;
    goBackModal.current = Modal.alert(
      intl.formatMessage({ id: '温馨提示' }),
      intl.formatMessage({ id: '確認取消申請嗎？' }),
      [
        {
          text: intl.formatMessage({ id: '取消' }),
          onPress: () => {
            goBackModal.current?.close();
            goBackModal.current = null;
          },
        },
        {
          text: intl.formatMessage({ id: '確認' }),
          onPress: () => {
            const params = {
              id: applicationId,
            };
            cacelApply(params)
              .then((data) => {
                if (data?.success) {
                  window.location.reload();
                } else {
                  Modal.alert(
                    intl.formatMessage({ id: '提示' }),
                    data.msg || ''
                  );
                }
              })
              .catch((error) => {
                console.error('获取列表失败:', error);
              })
              .finally(() => {
                setLoading(false);
              });
          },
        },
      ],
    );
  });

  useEffect(() => {
    if (!profile || !profile.euid) {
      setLoading(true);
      return undefined;
    }
    let isMounted = true;
    const eeuid = profile.euid;
    const params = {
      euid: eeuid,
    };
    setLoading(true);

    setTimeout(() => {
      fetchApplyList(params)
        .then((data) => {
          if (!isMounted) return;
          if (data != null) {
            setApplyList(data);
            if (data.length === 0) {
              window.location.href = '/foster-service?platform=personal';
            }
            const hasStatusOne = data.some(item => (item.flowStatus === 1 && item.cancelStatus === 0));
            if (hasStatusOne) {
              setIsAdd(false);
            }
          } else {
            window.location.href = '/foster-service?platform=personal';
          }
        })
        .catch((error) => {
          if (!isMounted) return;
          console.error('获取列表失败:', error);
        })
        .finally(() => {
          if (isMounted) {
            setLoading(false);
          }
        });
    }, 1000);
    return () => {
      isMounted = false;
    };
  }, [profile?.euid]);

  // 新增一个useEffect来处理跳转逻辑
  useEffect(() => {
    if (!loading && applyList.length === 0) {
      window.location.href = '/foster-service?platform=personal';
    }
  }, [loading, applyList.length]);


  return (
    <>
      <Helmet>
        <title>{intl.formatMessage({ id: '申請成為寄養父母' })}</title>
      </Helmet>
      <div className={styles.baseRoot}>
        <div className={styles.main} ref={scrollRef}>
          {loading ? (
            <div className={styles.loading}>
              <Spin size="large" />
            </div>
          ) : (
            <Space direction="vertical" size={24}>
              {applyList.length > 0 ? (
                applyList.map(item => (
                  <Card
                    key={item.id}
                    title={(
                      <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                        <span>{`${intl.formatMessage({ id: '申請編號:' })} ${item.appNo}`}</span>
                        {/* eslint-disable-next-line max-len */}
                        <span style={{
                          fontSize: '14px',
                          lineHeight: '30px',
                          // eslint-disable-next-line max-len
                          color: getStatusText(item.flowStatus, item.cancelStatus) === intl.formatMessage({ id: '評估完成' }) ? '#1aa17d' : '#fec207',
                          border: `1px solid ${getStatusText(item.flowStatus, item.cancelStatus) === intl.formatMessage({ id: '評估完成' }) ? '#1aa17d' : '#fec207'}`,
                          padding: '0px 15px',
                          background: '#fff',
                          borderRadius: '15px',
                        }}
                        >
                        {getStatusText(item.flowStatus, item.cancelStatus)}
                      </span>
                      </div>
                    )}
                    style={{ background: '#d8ebe7' }}
                    bodyStyle={{ background: '#fff' }}
                  >
                    <div className={clsx(styles.paragr)}>
                      <p>
                        <span className={clsx(styles.spanLeft)}>{intl.formatMessage({ id: '申請人姓名' })}</span>
                        <span className={clsx(styles.spanRight)}>{item.applicantName}</span>
                      </p>
                      <p>
                        <span className={clsx(styles.spanLeft)}>{intl.formatMessage({ id: '配偶姓名' })}</span>
                        <span className={clsx(styles.spanRight)}>{item.spouseName}</span>
                      </p>
                      <p>
                        <span className={clsx(styles.spanLeft)}>{intl.formatMessage({ id: '申請時間' })}</span>
                        <span className={clsx(styles.spanRight)}>
                          {item.submitTime ? formatDateTime2(item.submitTime) : item.submitTime}
                        </span>
                      </p>
                      <p>
                        <span className={clsx(styles.spanLeft)}>{intl.formatMessage({ id: '聯繫電話' })}</span>
                        <span className={clsx(styles.spanRight)}>{item.applicantAreano}-{item.applicantCellphone}</span>
                      </p>
                      <p>
                        <span className={clsx(styles.spanLeft)}>{intl.formatMessage({ id: '地址' })}</span>
                        <span className={clsx(styles.spanRightAddr)}>{item.applicantAddress}</span>
                      </p>
                      <p className={clsx(styles.btnP)}>
                        <Button
                          type="primary"
                          className={clsx(styles.signBtn)}
                          onClick={() => {
                            // 跳转到 NurseryOpendayDetail.tsx 並傳遞參數
                            window.location.href = `/foster-apply-detail?platform=personal&id=${item.id}`;
                          }}
                        >
                          {intl.formatMessage({ id: '查看詳情' })}
                        </Button>
                        {(item.cancelStatus !== 1 && item.flowStatus === 1) && (
                          <Button
                            type="default"
                            className={clsx(styles.cancelBtn)}
                            onClick={() => {
                              handleCancelApply(item.id);
                            }}
                          >
                            {intl.formatMessage({ id: '取消申請' })}
                          </Button>
                        )}
                      </p>
                    </div>
                  </Card>
                ))
              ) : null }
            </Space>
          )}
        </div>
        {/* 底部的上一步下一步按鈕 */}
        {!loading && applyList.length > 0 ? (
          <HandleButton
            rightButton={{
              text: intl.formatMessage({ id: '新申請' }),
              onClick: () => {
                if (isAdd) {
                  window.location.href = '/foster-service?platform=personal';
                } else {
                  Modal.alert(
                    intl.formatMessage({ id: '溫馨提示' }),
                    intl.formatMessage({ id: '在評估未完成之前，不可新增申請。' }),
                    [
                      {
                        text: intl.formatMessage({ id: '知道了' }),
                      },
                    ]
                  );
                }
              },
            }}
            className={styles.smallHandleButton}
          />
        ) : null}
      </div>
    </>
  );
}

export default ApplyRecoedService;
